import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { MFA_REQUIRED_EVENT } from '../../api/mfaRequired';
import { Button } from '../ui/Button';

/** The enrolment page — also where this notice never shows. */
export const TWO_FACTOR_PATH = '/security/two-factor';

/**
 * Shown when the API refused something for want of a second factor.
 *
 * Compliance, approvals and payroll all sit behind the backend's MFA guard,
 * reads included, so an admin who has not enrolled lands on a page whose
 * every request is a 403. This says what that is and where to fix it, in
 * place of a page of load errors.
 *
 * It clears when the route changes: the next page asks again, and only shows
 * the notice if it is refused too.
 */
export const MfaRequiredNotice: React.FC = () => {
  const [needed, setNeeded] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const show = () => setNeeded(true);
    window.addEventListener(MFA_REQUIRED_EVENT, show);
    return () => window.removeEventListener(MFA_REQUIRED_EVENT, show);
  }, []);

  useEffect(() => setNeeded(false), [pathname]);

  if (!needed || pathname === TWO_FACTOR_PATH) return null;

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        marginBottom: 20,
        background: 'var(--warning-100)',
        border: '1px solid var(--warning-600)',
        borderRadius: 'var(--radius-lg)',
        flexWrap: 'wrap',
      }}
    >
      <ShieldCheck size={20} color="var(--warning-900)" style={{ flexShrink: 0 }} />
      <div style={{ flex: '1 1 280px', minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--warning-900)' }}>
          This page needs two-factor authentication
        </div>
        <div style={{ marginTop: 2, fontSize: 13, color: 'var(--neutral-700)' }}>
          Compliance, approvals and payroll ask for a code from an authenticator
          app as well as your password. Set it up once and this page opens.
        </div>
      </div>
      <Button size="sm" onClick={() => navigate(TWO_FACTOR_PATH)}>
        Set up two-factor
      </Button>
    </div>
  );
};
